/**
 * PortalInvoiceSubmit
 * -------------------
 * Suppliers and contractors bill Sterile Mate from their own portal. What they
 * send lands as "pending approval" — it is not in the ledger until a human
 * says so, and they never see or touch a payment record.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Paperclip, Plus, FileText, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_UI = {
  pending_approval: { cls: "bg-amber-100 text-amber-800", label: "Awaiting approval", Icon: Clock },
  received: { cls: "bg-indigo-50 text-indigo-700", label: "Accepted", Icon: CheckCircle2 },
  approved: { cls: "bg-indigo-50 text-indigo-700", label: "Approved", Icon: CheckCircle2 },
  partially_paid: { cls: "bg-orange-50 text-orange-700", label: "Partially paid", Icon: CheckCircle2 },
  paid: { cls: "bg-emerald-50 text-emerald-700", label: "Paid", Icon: CheckCircle2 },
  rejected: { cls: "bg-red-50 text-red-700", label: "Rejected", Icon: XCircle },
  disputed: { cls: "bg-red-50 text-red-700", label: "Disputed", Icon: XCircle },
  void: { cls: "bg-slate-100 text-slate-500", label: "Void", Icon: XCircle },
};

export const PortalInvoiceSubmit = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const fileInput = useRef(null);
  const [form, setForm] = useState({
    invoice_number: "", amount: "", invoice_date: "", due_date: "",
    terms: "Net 30", po_number: "", reference: "",
  });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/payables/portal/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.invoices || []);
    } catch (e) {
      // A portal account with no invoices yet is not an error worth shouting about.
      setRows([]);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.invoice_number.trim()) return toast.error("Enter your invoice number");
    if (Number(form.amount) <= 0) return toast.error("Enter the invoice amount");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("file", file);
      const r = await axios.post(`${API}/api/payables/portal/invoices`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(r.data?.message || "Invoice submitted");
      setOpen(false);
      setFile(null);
      setForm({ invoice_number: "", amount: "", invoice_date: "", due_date: "", terms: "Net 30", po_number: "", reference: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit that invoice");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="portal-invoices">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-600" /> Invoices you have submitted
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Bill us directly — upload the invoice and we will confirm once it is approved for payment.
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-cyan-600 hover:bg-cyan-700 gap-1.5"
          data-testid="portal-invoice-new">
          <Plus className="w-4 h-4" /> Submit an invoice
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center">
          <p className="text-sm text-slate-500">No invoices submitted yet.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {rows.map(inv => {
            const ui = STATUS_UI[inv.status] || { cls: "bg-slate-100 text-slate-600", label: inv.status, Icon: Clock };
            const { Icon } = ui;
            return (
              <div key={inv.id} className="px-3 py-2.5 bg-white" data-testid={`portal-invoice-${inv.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-sm font-bold text-slate-900">#{inv.invoice_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase inline-flex items-center gap-1 ${ui.cls}`}>
                    <Icon className="w-3 h-3" /> {ui.label}
                  </span>
                  <span className="text-xs text-slate-500">{inv.invoice_date || ""}</span>
                  <span className="flex-1" />
                  <span className="text-sm font-black text-slate-900">{money(inv.amount)}</span>
                  {inv.paid_total > 0 && (
                    <span className="text-xs text-emerald-700">{money(inv.paid_total)} paid</span>
                  )}
                </div>
                {inv.rejection_reason && (
                  <p className="text-[11px] text-red-700 mt-1">Rejected: {inv.rejection_reason}</p>
                )}
                {(inv.attachments || []).map(a => (
                  <a key={a.url} href={a.url} target="_blank" rel="noreferrer"
                    className="text-[11px] text-cyan-700 underline inline-flex items-center gap-1 mt-1 mr-3">
                    <Paperclip className="w-3 h-3" />{a.name}
                  </a>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center overflow-y-auto p-4"
          onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}
            data-testid="portal-invoice-dialog">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-black text-slate-900">Submit an invoice</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                It goes to Sterile Mate for approval before it enters the payables ledger.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Invoice #</label>
                  <Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)}
                    className="h-9 text-sm font-mono" data-testid="portal-invoice-number" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Amount</label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                    className="h-9 text-sm" data-testid="portal-invoice-amount" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Invoice date</label>
                  <Input type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)}
                    className="h-9 text-sm" data-testid="portal-invoice-date" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Due date</label>
                  <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)}
                    className="h-9 text-sm" data-testid="portal-invoice-due" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Terms</label>
                  <Input value={form.terms} onChange={(e) => set("terms", e.target.value)}
                    className="h-9 text-sm" data-testid="portal-invoice-terms" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Our PO # (if you have it)</label>
                  <Input value={form.po_number} onChange={(e) => set("po_number", e.target.value)}
                    className="h-9 text-sm font-mono" data-testid="portal-invoice-po" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                  What it covers
                </label>
                <Textarea value={form.reference} onChange={(e) => set("reference", e.target.value)} rows={2}
                  placeholder="Job, order or product this invoice is for"
                  className="text-sm" data-testid="portal-invoice-reference" />
              </div>
              <div className="rounded-md border border-cyan-200 bg-cyan-50/60 px-3 py-2.5">
                <input type="file" ref={fileInput} className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                  data-testid="portal-invoice-file-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileInput.current?.click()}
                  className="h-7 text-[11px] gap-1.5 bg-white" data-testid="portal-invoice-attach">
                  <Upload className="w-3 h-3" /> Attach the invoice PDF
                </Button>
                {file && (
                  <p className="text-[11px] text-cyan-900 mt-1.5 inline-flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {file.name}
                  </p>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="portal-invoice-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={submit} disabled={saving}
                className="bg-cyan-600 hover:bg-cyan-700 gap-1.5" data-testid="portal-invoice-submit">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Submit for approval
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalInvoiceSubmit;
